import React, { FunctionComponent, useState, MouseEvent, useEffect } from 'react';
import moment from 'moment';
import jsonpath from 'jsonpath';
import { v1 as uuid } from 'uuid';
import { cloneDeepWith, sum, round, isEqual } from 'lodash';
import EditIcon from '@material-ui/icons/Edit';
import gray from '@material-ui/core/colors/grey';
import CloseIcon from '@material-ui/icons/Close';
import DownloadIcon from '@material-ui/icons/GetApp';
import { Skeleton } from '@material-ui/lab';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    makeStyles,
    Menu,
    MenuItem,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@material-ui/core';
import DaysOfWeek from './DaysOfWeek';
import Modal from '../../../components/Modal';
import TimeSheetEntry from './TimeSheetEntry';
import TimeExpenseEntry from './TimeExpenseEntry';
import { Event } from '../../../store/modules/calendar/types';
import TimeSheetComment from './TimeSheetComment/TimeSheetComment';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import { TIME_SHEET_STATUS_IN_PROGRESS, TIME_SHEET_STATUS_SUBMITTED } from '../../../store/app.constants';
import { CommentList, FilterCondition, Form, MergeTemplate } from '../../../store/modules/application/types';
import FormFieldDisplay from '../../../components/FormDisplay/FormFieldDisplay';
import {
    TimeSheetCategory,
    ExpenseCategory,
    TimeSheet,
    TimeCard,
    TimeSheetExpense,
    TimeEntry,
    TimesheetType,
} from '../../../store/modules/timeSheet/type';

type EditTimeSheetProps = {
    timeSheet: TimeSheet;
    startDate: string;
    endDate: string;
    loading: boolean;
    timeSheetDate: string;
    timesheetId: string;
    editForm: Form;
    isTimeSheetLoading: boolean;
    timeSheetAppointments: Event[];
    expenseCategories: ExpenseCategory[];
    timeSheetCommentList: CommentList;
    isExpenseCategoriesLoading: boolean;
    isTimeSheetCategoriesLoading: boolean;
    isTimeSheetAppointmentsLoading: boolean;
    timeSheetCategories: TimeSheetCategory[];
    setSkip: Function;
    saveTimeSheet: Function;
    addTimeSheetComment: Function;
    getTimesheetComment: Function;
    handleAddTimeSheetModalStatus: Function;
    getAppointmentsBySelectedDate: Function;
    mergeTimesheetDocuments: Function;
    mergeTemplatesList: MergeTemplate[];
    isInspectorPackLoading: boolean;
    timesheetTypes: TimesheetType[];
    listFilters: FilterCondition[];
};

const useStyles = makeStyles((theme) => ({
    modalBtn: {
        marginLeft: 0,
        marginRight: '10px',
    },
    rightAlign: {
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
    },
    leftAlign: {
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-start',
    },
    timeSheetCards: {
        display: 'flex',
        width: '100%',
        overflowX: 'auto',
        marginLeft: 'auto',
        marginRight: 'auto',
        paddingBottom: 10,
    },
    title: {
        fontSize: 12,
        fontWeight: 600,
        color: '#0277BD',
        padding: 10,
    },
    disabledText: {
        fontSize: 12,
        fontWeight: 600,
        color: '#cfd8dc',
        padding: 10,
    },
    timeContainer: {
        padding: 25,
        marginTop: 10,
    },
    expenseContainer: {
        padding: 25,
        marginTop: 10,
    },
    commentContainer: {
        padding: 25,
        marginTop: 10,
    },
    locationContainer: {
        display: 'flex',
        padding: 25,
        marginTop: 10,
    },
    dialogContent: {
        minHeight: 400,
    },
    tableHeaderCell: {
        fontSize: 14,
        color: '#484848',
        fontWeight: 500,
        padding: 10,
    },
    dateRangeAndStatus: {
        padding: 25,
    },
    label: {
        fontSize: 14,
        fontWeight: 500,
        color: theme.palette.text.secondary,

        textAlign: 'right',
        marginRight: theme.spacing(2),
    },
    timeSheetDate: {
        fontSize: 14,
        fontWeight: 500,
        color: theme.palette.text.secondary,

        textAlign: 'left',
    },
    status: {
        fontSize: 14,
        fontWeight: 500,
        color: theme.palette.text.secondary,

        textAlign: 'right',
        marginRight: theme.spacing(2),
    },
    selectInput: {
        fontSize: 13,
        border: '1px solid rgb(0,0,0,0.23) !important',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        borderRadius: 3,
        marginBottom: '5px',
        color: '#484848 !important',
        minWidth: 150,
    },
    tableRow: {
        verticalAlign: 'top',
        cursor: 'pointer',
        '&:hover ': {
            backgroundColor: '#eeeeee',
            transition: 'ease-in 200ms',
            '& button': {
                opacity: 1,
                transition: 'ease-in 200ms',
            },
        },
    },
    tableCommentRow: {
        '& > *': {
            borderBottom: 'unset',
        },
        verticalAlign: 'top',
        height: '10px',
        cursor: 'pointer',
        '&:hover ': {
            backgroundColor: '#eeeeee',
            transition: 'ease-in 200ms',
            '& button': {
                opacity: 1,
                transition: 'ease-in 200ms',
            },
        },
    },
    center: {
        justifyContent: 'center',
    },
    height: {
        height: '35px',
    },
    fullWidth: {
        width: '100%',
    },
    editIcon: {
        color: '#0277BD;',
        fontSize: '16px',
        lineHeight: '24px',
        textAlign: 'right',
        fontWeight: 600,
        padding: '0px',
        marginRight: '25px',
    },
    downloadIcon: {
        color: '#0277BD;',
        fontSize: '16px',
        lineHeight: '24px',
        textAlign: 'right',
        fontWeight: 600,
        padding: '0px',
        marginRight: '25px',
    },
    textField: {
        fontSize: 14,
        fontWeight: 450,
        color: gray[600],
        lineHeight: '21px',
        wordWrap: 'break-word',
        width: '100%',
    },
    textFieldInput: {
        fontSize: 13,
        border: '1px solid rgb(0,0,0,0.23) !important',
        padding: 10,
        borderRadius: 3,
        marginBottom: '5px',
        color: '#484848 !important',
    },
    commentLabel: {
        fontWeight: 500,
        color: '#424242',
        fontSize: '14px',
        padding: '5px',
    },
    timeSheetTitle: {
        color: '#484848',
        fontSize: '20px',
        fontWeight: 500,
        padding: '15px 20px',
        height: 'auto',
    },
    totalHours: {
        cursor: 'pointer',
        height: '58px',
        width: '12.5%',
        marginTop: 10,
        backgroundColor: '#0277BD',
        borderRadius: '0px',
        color: 'white',
        '& > .MuiCardContent-root': {
            padding: 8,
        },
    },
    dateTimeContainer: {
        display: 'flex',
        width: '100%',
    },
    dateStyle: {
        display: 'flex',
        justifyContent: 'start',
        marginRight: 50,
    },
    totalHoursTitle: {
        fontSize: 18,
        fontWeight: 'bold',
    },
    alertSubmittedStyle: {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
    },
    timeSheetStatusLabel: {
        fontSize: '14px',
        color: '#1E4620',
    },
    MenuList: {
        marginTop: 46,
    },
}));

export type ExpenseCard = {
    key?: string;
    category: {
        id: string;
        name: string;
    };
    amount: number | null;
    comments?: string;
};

export type TimeSheetCard = {
    key?: string;
    category: {
        id: string;
        name: string;
    };
    person?: {
        id: string;
        name: string;
    };

    license?: {
        id: string;
        type: {
            id: string;
            name: string;
        };
        licenseNumber?: string;
    };

    serviceReference?: {
        id: string;
        type: string;
    };

    startTime?: string;
    unitHours?: number;
    mileage?: number;
    comments?: string;
};

function createTimeSheetEntry(): TimeSheetCard {
    return {
        key: uuid(),
        category: {
            id: '',
            name: '',
        },
        person: {
            id: '',
            name: '',
        },
        serviceReference: {
            id: '',
            type: '',
        },
        startTime: '',
        unitHours: undefined,
        mileage: undefined,
        comments: '',
    };
}

function createTimeExpenseEntry(): ExpenseCard {
    return {
        key: uuid(),
        category: {
            id: '',
            name: '',
        },
        amount: null,
        comments: '',
    };
}

const EditTimeSheet: FunctionComponent<EditTimeSheetProps> = (props) => {
    const classes = useStyles();
    const {
        setSkip,
        loading,
        endDate,
        startDate,
        editForm,
        timeSheet,
        timesheetId,
        listFilters,
        saveTimeSheet,
        timeSheetDate,
        timesheetTypes,
        expenseCategories,
        isTimeSheetLoading,
        timeSheetCategories,
        addTimeSheetComment,
        getTimesheetComment,
        timeSheetCommentList,
        timeSheetAppointments,
        isExpenseCategoriesLoading,
        isTimeSheetCategoriesLoading,
        handleAddTimeSheetModalStatus,
        getAppointmentsBySelectedDate,
        isTimeSheetAppointmentsLoading,
        mergeTimesheetDocuments,
        mergeTemplatesList,
        isInspectorPackLoading,
    } = props;

    const [formData, setFormData] = useState<Record<string, any>>({});
    const [selectedDate, setSelectedTimeSheetDate] = useState<string | undefined>(undefined);
    const [dailyHourLimit, setDailyHourLimit] = useState<number>(0);
    const [timeSheetEntries, setTimeSheetEntries] = useState<TimeSheetCard[]>([
        createTimeSheetEntry(),
        createTimeSheetEntry(),
    ]);
    const [timeExpenseEntries, setTimeExpenseEntries] = useState<ExpenseCard[]>([
        createTimeExpenseEntry(),
        createTimeExpenseEntry(),
    ]);
    const [isDownloadMenuOpen, setIsDownloadMenuOpen] = React.useState(false);
    const [isMaxHourLimit, setIsMaxHourLimit] = useState<boolean>(false);
    const anchorRef = React.useRef<HTMLButtonElement>(null);

    const isTimeSheetEntryValid = timeSheetEntries.some((item: TimeSheetCard) => {
        return item.category.id && !!item.unitHours;
    });
    const isExpenseEntryValid = timeExpenseEntries.some((item: ExpenseCard) => {
        return item.category.id && !!item.amount;
    });

    const totalMileageAmount = timeSheetEntries.reduce((acc, item) => {
        const timeCategory = timeSheetCategories.find((timeCategory) => timeCategory.id === item.category.id);
        const mileageRate = selectedDate
            ? timeCategory?.mileageRates?.find(
                  (item) =>
                      (!item.startPeriod || item.startPeriod <= selectedDate) &&
                      (!item.endPeriod || item.endPeriod >= selectedDate),
              )
            : undefined;
        const amount = timeCategory && mileageRate ? (item?.mileage ? +item.mileage * mileageRate.rate : 0) : 0;
        return acc + amount;
    }, 0);

    const allowRecordToSubmit = isExpenseEntryValid || isTimeSheetEntryValid || totalMileageAmount > 0;

    const handleClose = () => {
        setIsDownloadMenuOpen(false);
    };

    const handleToggle = () => {
        setIsDownloadMenuOpen((prevOpen) => !prevOpen);
    };
    const handleDownload = (item: any) => {
        mergeTimesheetDocuments(item?.id, item?.name, timeSheet?.id);
        setIsDownloadMenuOpen(false);
    };

    const [prevTimeSheetEntries, setPrevTimeSheetEntries] = useState<TimeSheetCard[]>();
    const [prevTimeExpenseEntries, setPrevTimeExpenseEntries] = useState<ExpenseCard[]>();

    const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);
    const totalHours =
        timeSheet.timeCards && round(sum(jsonpath.query(timeSheet.timeCards, '$..timeEntries[*].unitHours')), 2);
    const isSelectedDateBetweenTimeSheetPeriod =
        (!isTimeSheetLoading &&
            moment(timeSheetDate).isBetween(
                moment(timeSheet.timeCards[0].date),
                moment(timeSheet.timeCards[timeSheet.timeCards.length - 1].date),
                null, // can be year, month .... the granularity of your comparison
                '[]',
            )) ||
        false;

    const timeEntryIdsWithComments = timeSheetEntries
        .filter((timeEntry) => !!timeEntry?.comments)
        .map((item) => (item.key ? item.key : ''));

    const isTimeSheetSubmitted = timeSheet.status === TIME_SHEET_STATUS_SUBMITTED;

    const setTimeEntriesPayload = () => {
        const list: TimeSheetCard[] = cloneDeepWith(timeSheetEntries);
        const timeSheet: TimeEntry[] = [];

        list.forEach((item) => {
            if (item.category.id) {
                item.key = undefined;
                timeSheet.push({
                    ...item,
                    startTime: item.startTime ? item.startTime : undefined,
                    mileage: item.mileage ? +item.mileage : undefined,
                    unitHours: item.unitHours ? +item.unitHours : undefined,
                    person: item.person?.id ? item.person : undefined,
                    serviceReference: item.serviceReference?.id ? item.serviceReference : undefined,
                    comments: item.comments || undefined,
                });
            }
        });
        return timeSheet;
    };
    const setTimeExpenseEntriesPayload = () => {
        const list: ExpenseCard[] = cloneDeepWith(timeExpenseEntries);
        const expenseCategories: TimeSheetExpense[] = [];
        list.forEach((item) => {
            if (item.category.id && item.amount) {
                item.key = undefined;
                expenseCategories.push({
                    ...item,
                    amount: +item.amount,
                    comments: item.comments || undefined,
                });
            }
        });
        return expenseCategories;
    };
    const setTimeCardPayload = () => {
        const timeSheetEntries = setTimeEntriesPayload();
        const timeExpenseEntries = setTimeExpenseEntriesPayload();
        const timeCards: TimeCard[] = [];
        timeSheet.timeCards.forEach((day) => {
            if (day.date === selectedDate) {
                timeCards.push({
                    ...day,
                    date: selectedDate,
                    timeEntries: timeSheetEntries,
                    expenseEntries: timeExpenseEntries,
                    additionalData: { ...formData },
                });
            } else {
                day.date && timeCards.push({ ...day });
            }
        });
        return timeCards;
    };
    const handleSaveChange = (shouldPopupRemainOpen: boolean) => {
        if (!shouldPopupRemainOpen) {
            handleAddTimeSheetModalStatus(shouldPopupRemainOpen);
        }
        const timeCards: TimeCard[] = setTimeCardPayload();
        setSkip(0);
        saveTimeSheet(
            { id: timeSheet.id, status: TIME_SHEET_STATUS_IN_PROGRESS, timeCards },
            timeSheet.id,
            'Your timesheet has been saved',
            listFilters,
        );
    };
    const handleSubmitChange = () => {
        handleAddTimeSheetModalStatus(false);
        const timeCards: TimeCard[] = setTimeCardPayload();
        setSkip(0);
        saveTimeSheet(
            {
                id: timeSheet.id,
                status: TIME_SHEET_STATUS_SUBMITTED,
                timeCards,
            },
            timeSheet.id,
            'Your timesheet has been submitted',
            listFilters,
        );
    };
    const onClose = () => {
        setConfirmationModalOpen(false);
    };
    const onDismiss = () => {
        setConfirmationModalOpen(false);
        handleAddTimeSheetModalStatus(false);
    };

    const handleConfirmationModal = () => {
        const shouldTimeSheetModalRemainOpen =
            isEqual(timeSheetEntries, prevTimeSheetEntries) && isEqual(timeExpenseEntries, prevTimeExpenseEntries);
        // In case user doesn't change anything, TimeSheet Modal should be closed without a prompt Modal.
        shouldTimeSheetModalRemainOpen && handleAddTimeSheetModalStatus(!shouldTimeSheetModalRemainOpen);

        // In case user change anything,prompt modal should be shown.
        !shouldTimeSheetModalRemainOpen && setConfirmationModalOpen(true);
    };
    const handleSelectedDate = (selectedDateIndex: number) => {
        const selectedDate = moment(timeSheet.timeCards[selectedDateIndex].date).format('YYYY-MM-DD');
        setSelectedTimeSheetDate(selectedDate);
    };
    const handleTimeSheetEntry = (
        key: string,
        property:
            | 'category'
            | 'appointment'
            | 'person'
            | 'startTime'
            | 'mileage'
            | 'unitHours'
            | 'comments'
            | 'category'
            | 'expenseComments'
            | 'expenseAmount',

        value: any,
        index: number,
    ) => {
        const updatedTimeSheetEntries = [...timeSheetEntries];
        const timeEntry = updatedTimeSheetEntries?.find((entry) => entry.key === key);
        if (timeEntry) {
            switch (property) {
                case 'category':
                    const categoryName = timeSheetCategories.find((category) => category.id === value);
                    timeEntry.category.id = value || '';
                    timeEntry.category.name = categoryName?.name || '';
                    if (
                        updatedTimeSheetEntries[index - 1]?.startTime &&
                        updatedTimeSheetEntries[index - 1]?.unitHours
                    ) {
                        updatedTimeSheetEntries[index].startTime = moment(
                            `${selectedDate} ${updatedTimeSheetEntries[index - 1].startTime}`,
                        )
                            .add(updatedTimeSheetEntries[index - 1].unitHours, 'hours')
                            .format('HH:mm');
                    }

                    break;
                case 'appointment':
                    const appointmentName = timeSheetAppointments?.find((appointment) => appointment?.id === value);
                    if (timeEntry.serviceReference) {
                        timeEntry.serviceReference.id = value || '';
                        timeEntry.serviceReference.type = 'Event';
                    } else {
                        timeEntry.serviceReference = { id: value || '', type: 'Event' };
                    }
                    if (timeEntry.person) {
                        timeEntry.person.id =
                            (appointmentName?.persons && appointmentName?.persons.length > 1
                                ? ''
                                : appointmentName?.persons![0].id) || '';
                    } else {
                        timeEntry.person = {
                            id:
                                (appointmentName?.persons && appointmentName?.persons.length > 1
                                    ? ''
                                    : appointmentName?.persons![0].id) || '',
                            name: '',
                        };
                    }
                    break;
                case 'person':
                    timeEntry.person!.id = value;
                    break;
                case 'startTime':
                    timeEntry.startTime = value;
                    for (let i = index; i < updatedTimeSheetEntries.length; i++) {
                        const item = updatedTimeSheetEntries[i];
                        if (updatedTimeSheetEntries[i + 1]?.category.id && item.unitHours && item.startTime) {
                            updatedTimeSheetEntries[i + 1].startTime = moment(`${selectedDate} ${item.startTime}`)
                                .add(+item.unitHours, 'hours')
                                .format('HH:mm');
                        } else {
                            break;
                        }
                    }

                    break;
                case 'unitHours':
                    timeEntry.unitHours = value ? +value : undefined;
                    for (let i = index; i < updatedTimeSheetEntries.length; i++) {
                        const item = updatedTimeSheetEntries[i];
                        if (updatedTimeSheetEntries[i + 1]?.category.id && item.unitHours && item.startTime) {
                            updatedTimeSheetEntries[i + 1].startTime = moment(`${selectedDate} ${item.startTime}`)
                                .add(+item.unitHours, 'hours')
                                .format('HH:mm');
                        } else {
                            break;
                        }
                    }

                    break;
                case 'mileage':
                    timeEntry.mileage = value ? +value : undefined;
                    break;
                case 'comments':
                    timeEntry.comments = value;
                    break;

                default:
                    return timeEntry;
            }
        }
        if (updatedTimeSheetEntries[updatedTimeSheetEntries.length - 1].category.id) {
            updatedTimeSheetEntries.push(createTimeSheetEntry());
        }
        setTimeSheetEntries(updatedTimeSheetEntries);
        return updatedTimeSheetEntries;
    };
    const handleTimeExpenseEntry = (
        key: string,
        property: 'expenseCategory' | 'expenseComments' | 'expenseAmount',

        value: any,
    ) => {
        const updatedTimeSheetEntries = [...timeExpenseEntries];
        const timeExpenseEntry = updatedTimeSheetEntries?.find((entry) => entry.key === key);
        if (timeExpenseEntry) {
            switch (property) {
                case 'expenseCategory':
                    const selectedExpenseCategory = expenseCategories.find((expense) => expense.id === value);
                    timeExpenseEntry.category.id = value || '';
                    timeExpenseEntry.category.name = selectedExpenseCategory?.name || '';
                    break;
                case 'expenseComments':
                    timeExpenseEntry.comments = value;
                    break;
                case 'expenseAmount':
                    timeExpenseEntry.amount = value ? +value : null;
                    break;
                default:
                    return timeExpenseEntry;
            }
        }
        if (updatedTimeSheetEntries[updatedTimeSheetEntries.length - 1].category.id) {
            updatedTimeSheetEntries.push(createTimeExpenseEntry());
        }
        setTimeExpenseEntries(updatedTimeSheetEntries);
        return updatedTimeSheetEntries;
    };
    const handleDeleteTimeSheetRow = (id: string, event: MouseEvent) => {
        event.stopPropagation();
        const filteredTimeEntries = timeSheetEntries.filter((item) => item.key !== id);
        setTimeSheetEntries(filteredTimeEntries);
    };
    const handleDeleteTimeExpenseRow = (id: string, event: MouseEvent) => {
        event.stopPropagation();
        const filteredTimeExpenseEntries = timeExpenseEntries.filter((item) => item.key !== id);
        setTimeExpenseEntries(filteredTimeExpenseEntries);
    };
    const handleInputChange = (dataKey: any, value: string) => {
        setFormData({
            ...formData,
            [dataKey]: value,
        });
    };
    const handleEdit = () => {
        saveTimeSheet({ id: timeSheet.id, status: TIME_SHEET_STATUS_IN_PROGRESS }, timeSheet.id, false);
    };

    const timeSheetUserId = timeSheet?.user?.id;

    useEffect(() => {
        if (!!timeSheet.id) {
            const selectedTimesheetType = timesheetTypes.find((type: any) => type.id === timeSheet.type.id);
            setDailyHourLimit(selectedTimesheetType?.dailyHourLimit ? selectedTimesheetType?.dailyHourLimit : 24);
        }
    }, [timeSheet, timesheetTypes]);

    // To check whether the selected date lies between the week range of current week or not
    useEffect(() => {
        if (!isTimeSheetLoading && !selectedDate) {
            !isSelectedDateBetweenTimeSheetPeriod
                ? setSelectedTimeSheetDate(timeSheet.timeCards[0].date)
                : setSelectedTimeSheetDate(timeSheetDate);
        }
    }, [isTimeSheetLoading, timeSheet.timeCards, timeSheetDate, isSelectedDateBetweenTimeSheetPeriod, selectedDate]);

    useEffect(() => {
        if (!isTimeSheetLoading && selectedDate) {
            const selectedTimeSheetDetails =
                timeSheet.timeCards && timeSheet.timeCards.find((timeCard) => timeCard.date === selectedDate);

            setFormData(selectedTimeSheetDetails?.additionalData ? selectedTimeSheetDetails?.additionalData : {});

            const updatedTimeExpenses =
                selectedTimeSheetDetails?.expenseEntries && selectedTimeSheetDetails?.expenseEntries.length
                    ? selectedTimeSheetDetails?.expenseEntries
                          .map((item) => {
                              return {
                                  ...item,
                                  amount: item.amount ? +item.amount : null,
                                  key: uuid(),
                              } as ExpenseCard;
                          })
                          .concat(createTimeExpenseEntry())
                    : [createTimeExpenseEntry(), createTimeExpenseEntry()];

            setPrevTimeExpenseEntries(cloneDeepWith(updatedTimeExpenses));
            setTimeExpenseEntries(updatedTimeExpenses);

            const updatedTimeEntries =
                selectedTimeSheetDetails?.timeEntries && selectedTimeSheetDetails?.timeEntries.length
                    ? selectedTimeSheetDetails?.timeEntries
                          .map((item) => {
                              return { ...item, key: uuid() } as TimeSheetCard;
                          })
                          //Always add a new empty object to show the user an extra row
                          .concat(createTimeSheetEntry())
                    : [createTimeSheetEntry(), createTimeSheetEntry()];

            setPrevTimeSheetEntries(cloneDeepWith(updatedTimeEntries));
            setTimeSheetEntries(updatedTimeEntries);
        }
    }, [selectedDate, isTimeSheetLoading, timeSheet.timeCards]);

    useEffect(() => {
        if (!isTimeSheetLoading && selectedDate) getAppointmentsBySelectedDate(selectedDate, timeSheetUserId);
    }, [selectedDate, isTimeSheetLoading, getAppointmentsBySelectedDate, timeSheetUserId]);

    const Loader = <Skeleton width="50%" />;

    return (
        <>
            <DialogTitle>
                <Grid container>
                    <Grid item xs={6} className={classes.leftAlign}>
                        <Typography className={classes.timeSheetTitle}>Timesheet</Typography>
                    </Grid>
                    <Grid item xs={6} className={classes.rightAlign}>
                        <IconButton title="Close" style={{ color: '#484848' }} onClick={handleConfirmationModal}>
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                </Grid>
            </DialogTitle>
            <DialogContent>
                <Box className={classes.dateRangeAndStatus}>
                    <Grid item xs={12} sm={12} md={12} lg={12} style={{ display: 'flex', alignItems: 'center' }}>
                        <Grid
                            item
                            xs={6}
                            sm={6}
                            md={6}
                            lg={6}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}
                        >
                            {!startDate && !endDate ? (
                                Loader
                            ) : (
                                <Typography className={classes.timeSheetDate} component="h1">
                                    {`${moment(startDate).format('L')} -  ${moment(endDate).format('L')}`}
                                </Typography>
                            )}
                        </Grid>
                        <Grid
                            item
                            xs={6}
                            sm={6}
                            md={6}
                            lg={6}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
                        >
                            {isTimeSheetLoading ? (
                                Loader
                            ) : (
                                <>
                                    {!(timeSheet.status === TIME_SHEET_STATUS_IN_PROGRESS) && (
                                        <>
                                            <Button
                                                className={classes.editIcon}
                                                ref={anchorRef}
                                                aria-controls={isDownloadMenuOpen ? 'menu-list-grow' : undefined}
                                                aria-haspopup="true"
                                                onClick={handleToggle}
                                                disabled={isInspectorPackLoading}
                                            >
                                                <DownloadIcon />
                                                <Typography
                                                    className={
                                                        isInspectorPackLoading ? classes.disabledText : classes.title
                                                    }
                                                >
                                                    Download
                                                </Typography>
                                            </Button>
                                            <Menu
                                                id="simple-menu"
                                                anchorEl={anchorRef.current}
                                                keepMounted
                                                open={isDownloadMenuOpen}
                                                onClose={handleClose}
                                                className={classes.MenuList}
                                            >
                                                {mergeTemplatesList?.map((item: any) => (
                                                    <MenuItem key={item?.id} onClick={() => handleDownload(item)}>
                                                        {item?.name}
                                                    </MenuItem>
                                                ))}
                                            </Menu>
                                            {isTimeSheetSubmitted && (
                                                <Button className={classes.editIcon} onClick={handleEdit}>
                                                    <EditIcon />
                                                    <Typography className={classes.title}>EDIT</Typography>
                                                </Button>
                                            )}
                                        </>
                                    )}
                                    <Typography className={classes.label}>Status:</Typography>
                                    <Typography className={classes.status}>{timeSheet.status}</Typography>
                                </>
                            )}
                        </Grid>
                    </Grid>
                </Box>
                <Box style={{ padding: ' 0 25px' }}>
                    <Box style={{ display: ' flex' }}>
                        <Box className={classes.timeSheetCards}>
                            {isTimeSheetLoading
                                ? Loader
                                : timeSheet.timeCards &&
                                  timeSheet.timeCards.map((date, index) => {
                                      return (
                                          <DaysOfWeek
                                              index={index}
                                              key={date.date}
                                              timeSheet={timeSheet}
                                              handleSelectedDate={handleSelectedDate}
                                              date={moment(date.date).format('MMM D')}
                                              dayOfWeek={moment(date.date).format('ddd')}
                                              selectedDate={moment(selectedDate).format('MMM D')}
                                          />
                                      );
                                  })}
                            {isTimeSheetLoading ? (
                                Loader
                            ) : (
                                <Card className={classes.totalHours}>
                                    <CardContent>
                                        <Typography className={classes.totalHoursTitle} component="h1">
                                            {totalHours}
                                        </Typography>
                                        <Box className={classes.dateTimeContainer}>
                                            <Typography component="h3" className={classes.dateStyle}>
                                                TOTAL HOURS
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            )}
                        </Box>
                    </Box>
                </Box>
                <Box className={classes.timeContainer}>
                    <TimeSheetEntry
                        hourLimit={dailyHourLimit}
                        selectedDate={selectedDate}
                        timeSheetEntries={timeSheetEntries}
                        setIsMaxHourLimit={setIsMaxHourLimit}
                        timeSheetCategories={timeSheetCategories}
                        handleTimeSheetEntry={handleTimeSheetEntry}
                        timeSheetAppointments={timeSheetAppointments}
                        handleDeleteTimeSheetRow={handleDeleteTimeSheetRow}
                        timeEntryIdsWithComments={timeEntryIdsWithComments}
                        isTimeSheetCategoriesLoading={isTimeSheetCategoriesLoading}
                        readOnly={timeSheet.status !== TIME_SHEET_STATUS_IN_PROGRESS}
                        isTimeSheetAppointmentsLoading={isTimeSheetAppointmentsLoading}
                    />
                </Box>
                <Box className={classes.expenseContainer}>
                    <TimeExpenseEntry
                        totalMileageAmount={totalMileageAmount}
                        readOnly={timeSheet.status !== TIME_SHEET_STATUS_IN_PROGRESS}
                        expenseCategories={expenseCategories}
                        timeExpenseEntries={timeExpenseEntries}
                        handleTimeExpenseEntry={handleTimeExpenseEntry}
                        handleDeleteTimeExpenseRow={handleDeleteTimeExpenseRow}
                        isExpenseCategoriesLoading={isExpenseCategoriesLoading}
                    />
                </Box>
                {editForm && editForm.sections.length ? (
                    <Box className={classes.locationContainer}>
                        {editForm?.sections.map((section) => {
                            return section.entries.map((entry) => {
                                return (
                                    <Grid key={entry.field} container style={{ marginTop: '10px', width: '100%' }}>
                                        <Grid
                                            key={entry.field}
                                            item
                                            style={{ display: 'flex', alignItems: 'center', width: '100%' }}
                                        >
                                            <FormFieldDisplay
                                                formData={formData}
                                                formField={entry}
                                                value={formData[entry.field]}
                                                readOnly={timeSheet.status !== TIME_SHEET_STATUS_IN_PROGRESS}
                                                handleInputChange={handleInputChange}
                                            />
                                        </Grid>
                                    </Grid>
                                );
                            });
                        })}
                    </Box>
                ) : (
                    <></>
                )}

                <Box className={classes.commentContainer}>
                    <Typography className={classes.commentLabel}>Comment</Typography>
                    <TimeSheetComment
                        loading={loading}
                        timesheetId={timesheetId}
                        addTimeSheetComment={addTimeSheetComment}
                        timeSheetCommentList={timeSheetCommentList}
                        getTimesheetComment={getTimesheetComment}
                        readOnly={timeSheet.status !== TIME_SHEET_STATUS_IN_PROGRESS}
                    />
                </Box>
            </DialogContent>
            {timeSheet.status === TIME_SHEET_STATUS_IN_PROGRESS && (
                <DialogActions>
                    <Button
                        onClick={() => {
                            handleSubmitChange();
                        }}
                        className={classes.modalBtn}
                        disabled={!allowRecordToSubmit || isMaxHourLimit}
                    >
                        SUBMIT
                    </Button>
                    <Button
                        className={classes.modalBtn}
                        onClick={() => handleSaveChange(true)}
                        variant="contained"
                        color="primary"
                        disabled={isMaxHourLimit}
                    >
                        SAVE
                    </Button>
                    <Button
                        className={classes.modalBtn}
                        onClick={() => handleSaveChange(false)}
                        variant="contained"
                        color="primary"
                        disabled={isMaxHourLimit}
                    >
                        SAVE & CLOSE
                    </Button>
                </DialogActions>
            )}

            {isConfirmationModalOpen && (
                <Modal open={true} maxWidth={'md'} fullWidth={false} minHeight={'10vh'}>
                    <ConfirmationDialog
                        onClose={onClose}
                        onDismiss={onDismiss}
                        content="You have changes that may not be saved. Do you want to leave without saving?"
                        contentTitle="Unsaved changes"
                    />
                </Modal>
            )}
        </>
    );
};

export default EditTimeSheet;
